import { setContext } from '@apollo/client/link/context';
import type { DocumentNode } from 'graphql';
import { print } from 'graphql';
import profile from '../profile';

// The GraphQL name grammar, limited to a sane length for use in headers.
const OPERATION_NAME_RE = /^[_A-Za-z][_0-9A-Za-z]{0,127}$/;

const queryHashCache = new WeakMap<DocumentNode, Promise<string | null>>();

async function computeQueryHash(query: DocumentNode): Promise<string | null> {
    // `crypto.subtle` is unavailable in non-secure contexts (plain-http dev),
    // and older supported browsers may not provide TextEncoder. The operation
    // name still identifies the request in either case.
    if (!window.crypto?.subtle || typeof TextEncoder === 'undefined') {
        return null;
    }

    const digest = await window.crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(print(query))
    );

    return [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, 16);
}

function getQueryHash(query: DocumentNode): Promise<string | null> {
    let hash = queryHashCache.get(query);

    if (!hash) {
        hash = computeQueryHash(query).catch(() => {
            // Hashing is best-effort telemetry and must never block the
            // GraphQL request. Do not retain failures so a later request can
            // retry if the crypto failure was transient.
            queryHashCache.delete(query);
            return null;
        });
        queryHashCache.set(query, hash);
    }

    return hash;
}

/**
 * Adds GraphQL context headers to every request so proxies (nginx / ALB) can
 * attribute traffic to an operation and user, even when the request never
 * reaches Django (e.g. 502s). See issue #9614.
 */
export const graphqlContextLink = setContext(async (operation, previousContext) => {
    const headers = (previousContext as { headers?: Record<string, string> }).headers ?? {};
    const contextHeaders: Record<string, string> = {};

    const { operationName } = operation;
    if (operationName && OPERATION_NAME_RE.test(operationName)) {
        contextHeaders['X-GraphQL-Operation-Name'] = operationName;
    }

    const queryHash = await getQueryHash(operation.query);
    if (queryHash) {
        contextHeaders['X-GraphQL-Query-Hash'] = queryHash;
    }

    // Read lazily: the memoized Apollo client can be created before the
    // profile module has loaded the user data from Django.
    if (Number.isInteger(profile.userId) && (profile.userId as number) > 0) {
        contextHeaders['X-User-Id'] = String(profile.userId);
    }

    return { headers: { ...headers, ...contextHeaders } };
});
